import React, { useEffect, useState } from "react";

/**
 * Green leaf that sits on the "i" of "navi" (exactly like the company logo).
 * Drawn as SVG so it stays sharp at every size and needs no image file.
 */
export function BrandLeaf() {
  return (
    <svg className="navi-leaf" viewBox="0 0 24 28" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="naviLeafGrad" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#2f9e44" />
          <stop offset="1" stopColor="#8fd14f" />
        </linearGradient>
      </defs>
      <clipPath id="naviLeafClip"><path d="M3 25C1.5 14 7 4 21 2c1 11-4 21-18 23z" /></clipPath>
      <path d="M3 25C1.5 14 7 4 21 2c1 11-4 21-18 23z" fill="url(#naviLeafGrad)" />
      <g clipPath="url(#naviLeafClip)"><g transform="rotate(25 12 14)"><rect className="navi-leaf-glint" x="-8" y="-6" width="6" height="40" fill="#fff" /></g></g>
      <path d="M4.5 23.5C9 17 13 11.5 18 6.5" fill="none" stroke="rgba(255,255,255,.55)" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * NAVI CARE brand: the official wordmark (bold) with the leaf and a small, light "SERVICES" line under it.
 *  - "navi" is a mask painted with the surrounding text colour: dark on light pages, white on dark pages / over photos
 *  - "care" keeps the brand blue; the leaf keeps brand green and sways (rotates) gently with a white shine
 *  - `services` adds the small "SERVICES" line
 * Size it with font-size (the logo is 1.45em tall). `shine` adds a soft glow around the leaf.
 * The accessible name is "NAVI CARE Services"; the artwork itself is decorative.
 */
// Images live in /public and are served as-is, so the mask URLs are set here instead of in CSS.
const NAVI_MASK = { WebkitMaskImage: "url(/navi-care-navi.png)", maskImage: "url(/navi-care-navi.png)" };
const LEAF_MASK = { WebkitMaskImage: "url(/navi-care-leaf.png)", maskImage: "url(/navi-care-leaf.png)" };

// If the official logo image cannot load (offline, blocked, missing), a plain-text name is shown instead of an empty gap.
let LOGO_BROKEN = false;

export default function BrandName({ services = false, shine = false, className = "", style, mark = true }) {
  const [broken, setBroken] = useState(LOGO_BROKEN);
  useEffect(() => {
    if (LOGO_BROKEN || typeof Image === "undefined") return undefined;
    const probe = new Image();
    probe.onerror = () => { LOGO_BROKEN = true; setBroken(true); };
    probe.src = "/navi-care-rest.png";
    return () => { probe.onerror = null; };
  }, []);
  const ncMark = mark ? <img className="navi-brand-nc-mark" src="/navi-nc-mark.png" alt="" width="96" height="96" decoding="async" fetchPriority="high" draggable="false" /> : null;
  const classes = `navi-brand-inline ${mark ? "has-nc-mark" : ""} ${className}`;
  if (broken) {
    return (
      <span className={`navi-brand ${classes}`} style={{ fontWeight: 800, letterSpacing: "-.02em", ...style }} role="img" aria-label="NAVI CARE Services">
        {ncMark}
        <span className="navi-brand-copy">
          <span className="navi-brand-fallback-word">NAVI <span style={{ color: "#0a66ff" }}>CARE</span></span>
          {services && <small className="navi-wm-tag">Services</small>}
        </span>
      </span>
    );
  }
  return (
    <span className={`navi-wordmark navi-brand ${classes} ${shine ? "is-shine" : ""}`} style={style} role="img" aria-label="NAVI CARE Services">
      {ncMark}
      <span className="navi-brand-copy">
        <span className="navi-wm" aria-hidden="true">
          <span className="navi-wm-navi" style={NAVI_MASK} />
          <img className="navi-wm-rest" src="/navi-care-rest.png" alt="" width="900" height="253" decoding="async" draggable="false" />
          <span className="navi-wm-leaf">
            <img src="/navi-care-leaf.png" alt="" width="160" height="148" decoding="async" draggable="false" />
            <i className="navi-wm-glint" style={LEAF_MASK} />
          </span>
        </span>
        {services && <span className="navi-wm-tag" aria-hidden="true">Services</span>}
      </span>
    </span>
  );
}
